import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { testSelectors } from '../../store/selectors/test';
import { useDispatch, useSelector } from 'react-redux';
import { authSelectors } from '../../store/selectors/auth';
import { checkTestThunk, continueTestThunk } from '../../store/thunks/test';
import actions from '../../store/actions';
import Button from '../../common/Buttons/Button';
import TestAnswer from './TestAnswer';
import { leaveTestStage } from '../../utils/testStage';

const PAIR_RESET_MS = 1000;
const MATCH_HIDE_MS = 650;
const AUTO_NEXT_MS = 750;

const vibrate = (pattern) => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(pattern);
  }
};

const TestConformity = ({ setOpenModal }) => {
  const dispatch = useDispatch();
  const auth = useSelector(authSelectors);
  const token = auth.token;

  const test = useSelector(testSelectors);
  const testInfo = test.test;
  const answers = test.answers;
  const definitions = test.definitions;
  const check = test.check;
  const error = test.error;
  const loadBtn = test.loadBtn;

  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [answersArr, setAnswersArr] = useState(answers);
  const [definitionArr, setDefinitionArr] = useState(definitions);
  const [copiedAnswersArr, setCopiedAnswersArr] = useState(answers);
  const [copiedDefinitionArr, setCopiedDefinitionArr] = useState(definitions);
  const [matchingIds, setMatchingIds] = useState([]);
  const [mismatchKey, setMismatchKey] = useState(0);

  // Один check на таск: раніше умова "всі пари складені" сиділа в ефекті, який
  // спрацьовує на кожну зміну виділення, і той самий запит летів 2-3 рази поспіль.
  const checkSentRef = useRef(false);
  const resetTimerRef = useRef(null);
  const matchTimersRef = useRef([]);
  const autoNextRef = useRef(null);
  const loadBtnRef = useRef(false);

  useEffect(() => {
    if (answers.length > 0 && definitions.length > 0) {
      setAnswersArr(answers);
      setDefinitionArr(definitions);
      setCopiedAnswersArr(answers);
      setCopiedDefinitionArr(definitions);
      setSelectedAnswer(null);
      setSelectedDefinition(null);
      setMatchingIds([]);
      matchTimersRef.current.forEach(clearTimeout);
      matchTimersRef.current = [];
      clearTimeout(autoNextRef.current);
      checkSentRef.current = false;
      dispatch(actions.test.checkTest({}));
      dispatch(actions.test.getTestAnswers([]));
      dispatch(actions.test.getTestDefinitions([]));
    }
  }, [answers, definitions]);

  useEffect(() => {
    if (!selectedAnswer || !selectedDefinition) return undefined;

    resetTimerRef.current = setTimeout(() => {
      setSelectedAnswer(null);
      setSelectedDefinition(null);
    }, PAIR_RESET_MS);

    return () => clearTimeout(resetTimerRef.current);
  }, [selectedAnswer, selectedDefinition]);

  // Всі пари складені — надсилаємо перевірку рівно один раз.
  useEffect(() => {
    if (
      !checkSentRef.current
      && answersArr.length > 0
      && copiedAnswersArr.length === 0
      && copiedDefinitionArr.length === 0
    ) {
      checkSentRef.current = true;
      dispatch(checkTestThunk({
        blockId: testInfo.el_homework_block_id,
        taskId: testInfo.homework_task_id,
        token,
      }));
    }
  }, [copiedAnswersArr, copiedDefinitionArr, answersArr]);

  useEffect(() => () => {
    clearTimeout(resetTimerRef.current);
    clearTimeout(autoNextRef.current);
    matchTimersRef.current.forEach(clearTimeout);
  }, []);

  const applySelection = (answerId, definitionId) => {
    if (answerId && definitionId && answerId === definitionId) {
      vibrate(35);
      setMatchingIds((prev) => [...prev, answerId]);
      setSelectedAnswer(null);
      setSelectedDefinition(null);

      const timer = setTimeout(() => {
        setCopiedAnswersArr((prev) => prev.filter((answer) => answer.id !== answerId));
        setCopiedDefinitionArr((prev) => prev.filter((definition) => definition.id !== definitionId));
        setMatchingIds((prev) => prev.filter((id) => id !== answerId));
      }, MATCH_HIDE_MS);
      matchTimersRef.current.push(timer);
      return;
    }

    if (answerId && definitionId) {
      vibrate([60, 40, 60]);
      setMismatchKey((value) => value + 1);
    }

    setSelectedAnswer(answerId);
    setSelectedDefinition(definitionId);
  };

  const handleCheckAnswer = (id) => {
    if (matchingIds.includes(id)) return;
    if (!copiedAnswersArr.some((answer) => answer.id === id)) return;

    if (selectedAnswer && selectedDefinition) {
      applySelection(id, null);
      return;
    }

    applySelection(selectedAnswer === id ? null : id, selectedDefinition);
  };

  const handleCheckDefinition = (id) => {
    if (matchingIds.includes(id)) return;
    if (!copiedDefinitionArr.some((definition) => definition.id === id)) return;

    if (selectedAnswer && selectedDefinition) {
      applySelection(null, id);
      return;
    }

    applySelection(selectedAnswer, selectedDefinition === id ? null : id);
  };

  loadBtnRef.current = loadBtn;

  const setTest = (id) => {
    if (loadBtnRef.current) return;

    clearTimeout(autoNextRef.current);
    leaveTestStage();

    dispatch(actions.test.getTestAnswers([]));
    dispatch(actions.test.getTestDefinitions([]));
    dispatch(actions.test.checkTest({}));
    localStorage.setItem('currentTestId', JSON.stringify(id));
    dispatch(continueTestThunk({ id, token }));
  };

  const setTestModal = () => {
    setOpenModal(true);
  };

  const allMatched = copiedAnswersArr.length === 0 && copiedDefinitionArr.length === 0;
  const isSolved = Object.keys(check)?.length > 0 && Boolean(check.is_correct) && allMatched && answersArr.length > 0;

  useEffect(() => {
    if (!isSolved || loadBtn) return undefined;

    autoNextRef.current = setTimeout(() => setTest(testInfo.el_homework_block_id), AUTO_NEXT_MS);

    return () => clearTimeout(autoNextRef.current);
  }, [isSolved, loadBtn]);

  return (
    <>
      <div
        className={`test__conformity ${answersArr.length === definitionArr.length ? 'test__conformity--grid' : ''}`.trim()}
        style={{ '--rows': answersArr.length || 1 }}
      >
        <>
            <div className="test__conformity-answers">
              {answersArr.map((answer, idx) => {
                const isMismatch = Boolean(selectedAnswer && selectedDefinition);
                const matched = !copiedAnswersArr.some(item => item.id === answer.id);
                const flashing = matchingIds.includes(answer.id);
                const isWrong = isMismatch && selectedAnswer === answer.id;

                return <TestAnswer
                  key={isWrong ? `${answer.id}-w${mismatchKey}` : answer.id}
                  count={idx + 1}
                  answer={answer.answer}
                  size='lg'
                  choosen={selectedAnswer === answer.id}
                  correct={matched || flashing}
                  incorrect={isWrong}
                  hidden={matched}
                  lang={testInfo.tts_lang}
                  onClick={() => handleCheckAnswer(answer.id)}
                />;
              })}
            </div>
            <div className="test__conformity-answers">
              {definitionArr.map((definition, idx) => {
                const isMismatch = Boolean(selectedAnswer && selectedDefinition);
                const matched = !copiedDefinitionArr.some(item => item.id === definition.id);
                const flashing = matchingIds.includes(definition.id);
                const isWrong = isMismatch && selectedDefinition === definition.id;

                return <TestAnswer
                  key={isWrong ? `${definition.id}-w${mismatchKey}` : definition.id}
                  count={idx + 1}
                  answer={definition.definition}
                  size='lg'
                  choosen={selectedDefinition === definition.id}
                  correct={matched || flashing}
                  incorrect={isWrong}
                  hidden={matched}
                  lang={testInfo.tts_lang}
                  onClick={() => handleCheckDefinition(definition.id)}
                />;
              })}
            </div>
        </>
      </div>

      {error && <div className="test__error error-message">{error}</div>}

      <div className="test__btns test__btns--sticky">
        <Button buttonBg='green' textSize='md' btnRadius='md'
          disabled={loadBtn || !isSolved}
          onClick={() => setTest(testInfo.el_homework_block_id)}
        >
          Далі
        </Button>
        <Button buttonBg='light' iconSvg='flag' iconColor='violet' onClick={() => setTestModal()}>
          Повідомити про помилку
        </Button>
      </div>
    </>
  );
};

TestConformity.propTypes = {
  setOpenModal: PropTypes.func,
};

export default TestConformity;
